/* eslint-disable max-lines */
import { faCaretDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Formik, FormikProps } from "formik";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { FormGroup } from "reactstrap";
import * as Yup from "yup";
import CheckoutFormTwoImage from "src/assets/images/checkout-step-2-bg.jpg";

import Button from "src/components/Button";
import Input from "src/components/Input";
import Select from "src/components/Select";
import CheckoutSidebar from "src/pages/OrderCreate";
import { postTestingParameters } from "src/store/actions/checkout";
import { getOrderById } from "src/store/actions/order";
import { useReducerData, useStoreActions } from "src/store/hooks";
import classes from "./styles.module.scss";
import {
  DECK_TYPE_OPTIONS,
  FormProps,
  OptionType,
  PROJECTILE_DIAMETER_OPTIONS,
  RAFTER_SPAN_OPTIONS,
  ROOF_LAYERS_OPTIONS,
  ROOF_PITCH_OPTIONS,
} from "./types";

const TestingParameters = () => {
  const navigate = useNavigate();
  const id = useReducerData("checkout", "activeCheckoutId", null);
  const [loading, setLoading] = useState(false);
  const [testingDetails, setTestingDetails] = useState<any>(null);

  const [showOtherDeckType, setShowOtherDeckType] = useState(false);
  const [isRoofPitchDropdownOpen, setIsRoofPitchDropdownOpen] = useState(false);
  const [isDeckTypeDropdownOpen, setIsDeckTypeDropdownOpen] = useState(false);

  const actions = useStoreActions({
    postTestingParameters,
    getOrderById,
  });

  const checkoutSchema = Yup.object().shape({
    projectile_diameter: Yup.object()
      .required("Please select projectile diameter")
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    rafter_span: Yup.object()
      .required("Please select rafter span")
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    deck_type_thickness: Yup.object()
      .required("Please select deck type/thickness")
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    other_deck_type_thickness: Yup.string().when("deck_type_thickness", {
      is: (val: OptionType) => val?.value === "Other - Fill in Blank",
      then: Yup.string().required("Please specify deck type"),
      otherwise: Yup.string().notRequired(),
    }),
    roof_layers: Yup.object()
      .required("Please select number of roof layers")
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
    roof_pitch: Yup.object()
      .required("Please select pitch of roof")
      .shape({
        value: Yup.string(),
        label: Yup.string(),
      })
      .nullable(),
  });

  useEffect(() => {
    const fetchOrder = async () => {
      if (id) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const { data } = await actions.getOrderById(id as string);
        setTestingDetails(data?.testing_parameters || null);
        if (
          data?.testing_parameters?.deck_type_thickness ===
          "Other - Fill in Blank"
        ) {
          setShowOtherDeckType(true);
        }
      }
    };
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Handle scrolling when dropdowns open (scroll down only for better UX)
  useEffect(() => {
    const formWrapper = document.querySelector(
      '[class*="formWrapper"]'
    ) as HTMLElement;
    if (formWrapper) {
      const isAnyDropdownOpen =
        isRoofPitchDropdownOpen || isDeckTypeDropdownOpen;

      if (isAnyDropdownOpen) {
        // Scroll the form wrapper down by 300px when any dropdown opens
        formWrapper.scrollBy({
          top: 300,
          behavior: "smooth",
        });
      }
    }
  }, [isRoofPitchDropdownOpen, isDeckTypeDropdownOpen]);

  // eslint-disable-next-line react/no-multi-comp
  const DropdownIndicator = () => (
    <span className={classes.caret}>
      <FontAwesomeIcon icon={faCaretDown} />
    </span>
  );

  const handleSubmit = async (values: FormProps) => {
    if (loading) return;
    setLoading(true);

    const finalValues = {
      order_id: id,
      projectile_diameter: values.projectile_diameter?.value || "",
      rafter_span: values.rafter_span?.value || "",
      deck_type_thickness: values.deck_type_thickness?.value || "",
      other_deck_type_thickness: values.other_deck_type_thickness || "",
      roof_layers: values.roof_layers?.value || "",
      roof_pitch: values.roof_pitch?.value || "",
    };

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const { status } = await actions.postTestingParameters(finalValues);
    setLoading(false);
    if (status === 1) {
      navigate(`/order/create/contact`);
    }
  };

  const handleDeckTypeChange = (
    selectedOption: OptionType,
    setFieldValue: any
  ) => {
    setFieldValue("deck_type_thickness", selectedOption);
    if (selectedOption?.value === "Other - Fill in Blank") {
      setShowOtherDeckType(true);
    } else {
      setShowOtherDeckType(false);
      setFieldValue("other_deck_type_thickness", "");
    }
  };

  // Find selected values for pre-population
  const selectedProjectileDiameter = PROJECTILE_DIAMETER_OPTIONS.find(
    (option: OptionType) => testingDetails?.projectile_diameter === option.value
  );
  const selectedRafterSpan = RAFTER_SPAN_OPTIONS.find(
    (option: OptionType) => testingDetails?.rafter_span === option.value
  );
  const selectedDeckType = DECK_TYPE_OPTIONS.find(
    (option: OptionType) => testingDetails?.deck_type_thickness === option.value
  );
  const selectedRoofLayers = ROOF_LAYERS_OPTIONS.find(
    (option: OptionType) => testingDetails?.roof_layers === option.value
  );
  const selectedRoofPitch = ROOF_PITCH_OPTIONS.find(
    (option: OptionType) => testingDetails?.roof_pitch === option.value
  );

  return (
    <CheckoutSidebar
      title="Testing Parameters"
      step={2}
      bgImage={CheckoutFormTwoImage}
      content="Tell us about the testing parameters for the property and the sample."
    >
      <div className={classes.wrapper}>
        <Formik
          enableReinitialize={true}
          onSubmit={handleSubmit}
          validationSchema={checkoutSchema}
          initialValues={{
            projectile_diameter:
              selectedProjectileDiameter || (null as unknown as OptionType),
            rafter_span: selectedRafterSpan || (null as unknown as OptionType),
            deck_type_thickness:
              selectedDeckType || (null as unknown as OptionType),
            other_deck_type_thickness:
              testingDetails?.other_deck_type_thickness || "",
            roof_layers: selectedRoofLayers || (null as unknown as OptionType),
            roof_pitch: selectedRoofPitch || (null as unknown as OptionType),
          }}
        >
          {({
            values,
            touched,
            errors,
            handleBlur,
            handleChange,
            isValid,
            dirty,
            setFieldValue,
            setFieldTouched,
          }: FormikProps<FormProps>) => (
            <Form className={`${classes.form} d-flex flex-column`}>
              <FormGroup className={classes.formGroup}>
                <Select
                  name="projectile_diameter"
                  label="Diameter of Projectile *"
                  placeholder="Select"
                  options={PROJECTILE_DIAMETER_OPTIONS}
                  value={values.projectile_diameter}
                  onChange={(newValue: unknown) => {
                    setFieldValue(
                      "projectile_diameter",
                      newValue as OptionType
                    );
                  }}
                  onBlur={() => setFieldTouched("projectile_diameter", true)}
                  error={errors.projectile_diameter as string}
                  touched={touched.projectile_diameter as unknown as boolean}
                  components={{ DropdownIndicator }}
                />
              </FormGroup>

              <FormGroup className={classes.formGroup}>
                <Select
                  name="rafter_span"
                  label="Rafter Span *"
                  placeholder="Enter rafter span in feet"
                  options={RAFTER_SPAN_OPTIONS}
                  value={values.rafter_span}
                  onChange={(newValue: unknown) => {
                    setFieldValue("rafter_span", newValue as OptionType);
                  }}
                  onBlur={() => setFieldTouched("rafter_span", true)}
                  error={errors.rafter_span as string}
                  touched={touched.rafter_span as unknown as boolean}
                  components={{ DropdownIndicator }}
                />
              </FormGroup>

              <FormGroup className={classes.formGroup}>
                <Select
                  name="deck_type_thickness"
                  label="Deck Type / Thickness *"
                  placeholder="Select"
                  options={DECK_TYPE_OPTIONS}
                  value={values.deck_type_thickness}
                  onChange={(newValue: unknown) => {
                    handleDeckTypeChange(newValue as OptionType, setFieldValue);
                  }}
                  onBlur={() => setFieldTouched("deck_type_thickness", true)}
                  onMenuOpen={() => setIsDeckTypeDropdownOpen(true)}
                  onMenuClose={() => setIsDeckTypeDropdownOpen(false)}
                  error={errors.deck_type_thickness as string}
                  touched={touched.deck_type_thickness as unknown as boolean}
                  components={{ DropdownIndicator }}
                />
              </FormGroup>

              <FormGroup
                className={`${classes.formGroup} ${classes.conditionalField} ${
                  !showOtherDeckType ? classes.hidden : ""
                }`}
              >
                <Input
                  inputGroupClassName={classes.inputGroupStyle}
                  required={true}
                  label="Other Deck Type"
                  value={values.other_deck_type_thickness}
                  placeholder="Please specify deck type"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={errors.other_deck_type_thickness}
                  touched={touched.other_deck_type_thickness}
                  name="other_deck_type_thickness"
                />
              </FormGroup>

              <FormGroup className={classes.formGroup}>
                <Select
                  name="roof_layers"
                  label="Number of Roof Layers *"
                  placeholder="Enter number of roof layers"
                  options={ROOF_LAYERS_OPTIONS}
                  value={values.roof_layers}
                  onChange={(newValue: unknown) => {
                    setFieldValue("roof_layers", newValue as OptionType);
                  }}
                  onBlur={() => setFieldTouched("roof_layers", true)}
                  error={errors.roof_layers as string}
                  touched={touched.roof_layers as unknown as boolean}
                  components={{ DropdownIndicator }}
                />
              </FormGroup>

              <FormGroup className={classes.formGroup}>
                <Select
                  name="roof_pitch"
                  label="Pitch of Roof *"
                  placeholder="Pitch of roof"
                  options={ROOF_PITCH_OPTIONS}
                  value={values.roof_pitch}
                  onChange={(newValue: unknown) => {
                    setFieldValue("roof_pitch", newValue as OptionType);
                  }}
                  onBlur={() => setFieldTouched("roof_pitch", true)}
                  onMenuOpen={() => setIsRoofPitchDropdownOpen(true)}
                  onMenuClose={() => setIsRoofPitchDropdownOpen(false)}
                  error={errors.roof_pitch as string}
                  touched={touched.roof_pitch as unknown as boolean}
                  components={{ DropdownIndicator }}
                />
              </FormGroup>

              <div className={classes.nextStep}>
                <Button
                  loading={loading}
                  disabled={!isValid || !dirty}
                  type="submit"
                  buttonText="Continue to Step 3"
                  rightIcon={<i className="fa fa-arrow-circle-o-right" />}
                />
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </CheckoutSidebar>
  );
};

export default TestingParameters;
